import React, { memo, useMemo } from 'react';
import { useWallet } from '@/contexts/WalletContext';
import { TokenData } from '@/types';
import { useTokenFormatting } from '@/utils/performanceOptimizations';
import styles from '@/styles/components/wallet/TokensList.module.css';

interface MemoizedTokensListProps {
  onRefresh?: () => Promise<void>;
}

/**
 * Memoized TokensList component optimized for performance
 * Prevents unnecessary re-renders when wallet state hasn't changed
 */
const MemoizedTokensList: React.FC<MemoizedTokensListProps> = memo(() => {
  const { state, isLoading } = useWallet();
  const { formatTokenBalance, formatUsdValue, formatPercentageChange, getPercentageChangeColor } = useTokenFormatting();

  // Memoize token items to prevent re-rendering when order doesn't change
  const tokenItems = useMemo(() => {
    return state.balance.tokens.map((token) => (
      <MemoizedTokenItem
        key={token.symbol}
        token={token}
        formatTokenBalance={formatTokenBalance}
        formatUsdValue={formatUsdValue}
        formatPercentageChange={formatPercentageChange}
        getPercentageChangeColor={getPercentageChangeColor}
      />
    ));
  }, [state.balance.tokens, formatTokenBalance, formatUsdValue, formatPercentageChange, getPercentageChangeColor]);

  return (
    <div className={styles.tokensSection}>
      <div className={styles.tokensHeader}>
        <div className={styles.tokensTitle}>Tokens</div>
      </div>

      <div className={styles.tokensList}>
        {tokenItems}
        
        {state.balance.tokens.length === 0 && !isLoading && (
          <div className={styles.emptyState}>
            No tokens found
          </div>
        )}
      </div>
    </div>
  );
});

// Memoized individual token item component
interface MemoizedTokenItemProps {
  token: TokenData;
  formatTokenBalance: (token: TokenData) => string;
  formatUsdValue: (usdValue: string) => string;
  formatPercentageChange: (percentageChange: string, isPositive: boolean) => string;
  getPercentageChangeColor: (isPositive: boolean) => string;
}

const MemoizedTokenItem: React.FC<MemoizedTokenItemProps> = memo(({
  token,
  formatTokenBalance,
  formatUsdValue,
  formatPercentageChange,
  getPercentageChangeColor,
}) => {
  // Memoize formatted values
  const formattedBalance = useMemo(() => formatTokenBalance(token), [token, formatTokenBalance]);
  const formattedUsdValue = useMemo(() => formatUsdValue(token.usdValue), [token.usdValue, formatUsdValue]);
  const formattedPercentageChange = useMemo(() => 
    formatPercentageChange(token.percentageChange, token.isPositiveChange), 
    [token.percentageChange, token.isPositiveChange, formatPercentageChange]
  );
  const percentageColor = useMemo(() => 
    getPercentageChangeColor(token.isPositiveChange), 
    [token.isPositiveChange, getPercentageChangeColor]
  );

  return (
    <div className={styles.tokenItem}>
      <div className={styles.tokenIconContainer}>
        <img
          src={token.icon}
          alt={token.name}
          className={styles.tokenIcon}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/icons/profile_page/wallet/more.svg';
          }}
        />
        <div className={styles.networkBadge}>
          <img
            src={token.networkIcon}
            alt={token.network}
            style={{ width: '12px', height: '12px' }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </div>
      </div>

      <div className={styles.tokenInfo}>
        <h4 className={styles.tokenName}>{token.name}</h4>
        <p className={styles.tokenBalance}>{formattedBalance}</p>
      </div>

      <div className={styles.tokenValue}>
        <p className={styles.tokenUsd}>{formattedUsdValue}</p>
        <p
          className={styles.tokenChange}
          style={{ color: percentageColor }}
        >
          {formattedPercentageChange}
        </p>
      </div>
    </div>
  );
});

MemoizedTokenItem.displayName = 'MemoizedTokenItem';
MemoizedTokensList.displayName = 'MemoizedTokensList';

export default MemoizedTokensList;